import React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/Auth/Auth';
import logo from '../../assets/lis-logo.png';
import Icon from '../Icon/Icon';
import './Navbar.css';

type HomeSectionId = 'inicio' | 'laboratorios' | 'comunidad' | 'recursos';

const homeLinks: { id: HomeSectionId; label: string; to: string }[] = [
  { id: 'inicio', label: 'Inicio', to: '/#inicio' },
  { id: 'laboratorios', label: 'Laboratorios', to: '/#laboratorios' },
  { id: 'comunidad', label: 'Comunidad', to: '/#comunidad' },
  { id: 'recursos', label: 'Recursos', to: '/#recursos' },
];

const routeLinks = [
  { label: 'Auxiliares', to: '/assistants' },
  { label: 'LIS-Presta', to: '/lispresta/' },
];

const isHomeSection = (value: string): value is HomeSectionId => homeLinks.some(({ id }) => id === value);

const Navbar = () => {
  const { username, isLoggedIn, clearContext } = React.useContext(AuthContext);
  const [open, setOpen] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState<HomeSectionId>('inicio');
  const [homeProgress, setHomeProgress] = React.useState(0);
  const location = useLocation();
  const isHome = location.pathname === '/';

  React.useEffect(() => setOpen(false), [location.pathname, location.hash]);

  React.useEffect(() => {
    document.body.classList.toggle('modal-open', open);
    const closeOnEscape = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.classList.remove('modal-open');
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  React.useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1160px)');
    const closeOnDesktop = (event: MediaQueryListEvent) => event.matches && setOpen(false);
    desktop.addEventListener?.('change', closeOnDesktop);
    return () => desktop.removeEventListener?.('change', closeOnDesktop);
  }, []);

  React.useEffect(() => {
    if (!isHome) return;
    const hashSection = location.hash.slice(1);
    if (isHomeSection(hashSection)) setActiveSection(hashSection);

    const visible = new Map<HomeSectionId, number>();
    const sections = homeLinks
      .map(({ id }) => document.getElementById(id))
      .filter((section): section is HTMLElement => Boolean(section));

    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const id = entry.target.id;
        if (!isHomeSection(id)) return;
        if (entry.isIntersecting) visible.set(id, entry.intersectionRatio);
        else visible.delete(id);
      });
      const current = Array.from(visible.entries()).sort((a, b) => b[1] - a[1])[0]?.[0];
      if (current) setActiveSection(current);
    }, { rootMargin: '-72px 0px -52% 0px', threshold: [0, 0.1, 0.25, 0.5, 0.75] });

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [isHome, location.hash]);

  React.useEffect(() => {
    if (!isHome) {
      setHomeProgress(0);
      return;
    }
    let frame = 0;
    const updateProgress = () => {
      frame = 0;
      const home = document.getElementById('home-page');
      if (!home) return;
      const start = home.offsetTop;
      const distance = Math.max(home.offsetHeight - window.innerHeight, 1);
      setHomeProgress(Math.min(1, Math.max(0, (window.scrollY - start) / distance)));
    };
    const requestUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(updateProgress);
    };
    updateProgress();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };
  }, [isHome]);

  const homeLinkClass = (id: HomeSectionId, mobile = false) => {
    const base = mobile ? 'mobile-link' : 'navbar-link';
    return `${base}${isHome && activeSection === id ? ` ${base}--active` : ''}`;
  };

  const navigationLinks = (mobile = false) => (
    <>
      {homeLinks.map(({ id, label, to }) => <Link key={id} to={to} className={homeLinkClass(id, mobile)} aria-current={isHome && activeSection === id ? 'location' : undefined}>{label}</Link>)}
      {routeLinks.map(({ label, to }) => (
        to === '/lispresta/' ? <a key={to} href={to} className={mobile ? 'mobile-link' : 'navbar-link'}>{label}</a> : <NavLink key={to} exact to={to} className={mobile ? 'mobile-link' : 'navbar-link'} activeClassName={mobile ? 'mobile-link--active' : 'navbar-link--active'}>{label}</NavLink>
      ))}
    </>
  );

  return (
    <>
      <a className="skip-link" href="#contenido-principal">Saltar al contenido</a>
      <header className="site-header">
        <div className="lis-container navbar">
          <Link className="navbar-brand" to="/" aria-label="Inicio — Laboratorio Integrado de Sistemas">
            <img src={logo} alt="" className="navbar-logo" />
            <span className="navbar-brand-copy"><strong>LIS</strong><span>Laboratorio Integrado de Sistemas</span></span>
          </Link>

          <nav className="desktop-nav" aria-label="Navegación principal">{navigationLinks()}</nav>

          <div className="desktop-actions">
            {isLoggedIn ? (
              <>
                <NavLink to="/changepassword" className="navbar-session-link" activeClassName="navbar-link--active" title={username}>Cambiar contraseña</NavLink>
                
                <Link to="/" className="lis-button lis-button--primary" onClick={clearContext}>Cerrar sesión</Link>
              </>
            ) : (
              <>
                <NavLink to="/login" className="lis-button lis-button--secondary" activeClassName="navbar-action--active">Iniciar sesión</NavLink>
                <NavLink to="/signup" className="lis-button lis-button--primary">Registrarse</NavLink>
              </>
            )}
          </div>

          <button type="button" className="mobile-menu-button" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-controls="mobile-navigation" aria-label={open ? 'Cerrar menú' : 'Abrir menú'}>
            <Icon name={open ? 'close' : 'menu'} />
          </button>
        </div>

        <div id="mobile-navigation" className="mobile-navigation" hidden={!open}>
          <nav className="lis-container mobile-navigation-inner" aria-label="Navegación principal móvil">
            {navigationLinks(true)}
            <div className="mobile-actions">
              {isLoggedIn ? (
                <><NavLink to="/changepassword" className="lis-button lis-button--secondary">Cambiar contraseña</NavLink><Link to="/" className="lis-button lis-button--primary" onClick={clearContext}>Cerrar sesión</Link></>
              ) : (
                <><NavLink to="/login" className="lis-button lis-button--secondary">Iniciar sesión</NavLink><NavLink to="/signup" className="lis-button lis-button--primary">Registrarse</NavLink></>
              )}
            </div>
          </nav>
        </div>
        {isHome && <span className="home-progress" aria-hidden="true" style={{ transform: `scaleX(${homeProgress})` }} />}
      </header>
    </>
  );
};

export default Navbar;
